import { Component } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { TherapyPlansService } from '../../../services/therapy-plans.service';
import * as moment from 'moment';
import {
  ChargeObject,
  SessionNameMapping,
  TherapyPaymentType,
} from '../../../models/TherapyPlans';
import { MasterdataService } from '../../../services/masterdata.service';
import { lastValueFrom } from 'rxjs';
import { TherapistExpertLevel } from 'projects/app-core/src/models/TherapistExpertLevel';
import { v4 as uuidv4 } from 'uuid';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { Router } from '@angular/router';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

export interface CreateTherapyPlanRequest {
  order: number;
  canPause: boolean;
  activeFrom?: number;
  activeUpto?: number;
  planName: string;
  planDescription: string;
  charges: Record<string, ChargeObject[]>;
  sessionNameMapping?: Record<string, SessionNameMapping>;
  paymentType: TherapyPaymentType;
  noOfSessions: number;
  validityDays: number;
  perSessionTimeInMin: number;
  maxPauseDays: number;
  planImage: string;
  internalPlanName: string;
  discoveryCall: boolean;
}

@Component({
  selector: 'app-create-therapy-plan',
  templateUrl: './create-therapy-plan.component.html',
  styleUrl: './create-therapy-plan.component.scss',
})
export class CreateTherapyPlanComponent extends AnyoComponent {
  planImage: File | undefined;

  async createPlan() {
    if (
      this.therapyPlanForm.get('canPause') &&
      this.therapyPlanForm.get('canPause')?.value &&
      !this.therapyPlanForm.get('canPause')?.value
    ) {
      this.toastService.showError('Please enter max pause days');
      return;
    }
    if (!this.validateSessionNameMapping()) {
      this.toastService.showError('Session name mapping are wrong');
      return;
    }
    if (!this.validatePlanCharges()) {
      this.toastService.showError('Session charges  are wrong');
      return;
    }
    this.pageLoading = true;
    const mappedCharges: Record<string, ChargeObject[]> = {};
    Object.keys(this.charges).forEach((key) => {
      const expertLevelId = this.therapistExpertLevel.find(
        (value) => value.data === key,
      )?.m_id;
      if (expertLevelId) {
        mappedCharges[expertLevelId] = this.charges[key];
      }
    });

    const mappedSessionNameMapping: Record<string, SessionNameMapping> = {};
    Object.keys(this.sessionNameMapping).forEach((_, index) => {
      mappedSessionNameMapping[index + 1] = this.sessionNameMapping[index];
    });
    if (this.therapyPlanForm.valid) {
      const createTherapyPlanRequest: CreateTherapyPlanRequest = {
        order: this.therapyPlanForm.get('order')?.value,
        internalPlanName: this.therapyPlanForm.get('internalPlanName')?.value,
        canPause: this.therapyPlanForm.get('canPause')?.value,
        activeFrom: Number(
          moment(this.therapyPlanForm.get('activeFrom')?.value, 'x'),
        ),
        activeUpto: Number(
          moment(this.therapyPlanForm.get('activeUpto')?.value, 'x'),
        ),
        planName: this.therapyPlanForm.get('planName')?.value,
        planDescription: this.therapyPlanForm.get('planDescription')?.value,
        paymentType: this.therapyPlanForm.get('paymentType')?.value,
        noOfSessions: this.therapyPlanForm.get('noOfSessions')?.value,
        validityDays: this.therapyPlanForm.get('validityDays')?.value,
        perSessionTimeInMin: this.therapyPlanForm.get('perSessionTimeInMin')
          ?.value,
        sessionNameMapping: mappedSessionNameMapping,
        charges: mappedCharges as unknown as Record<string, ChargeObject[]>,
        maxPauseDays: this.therapyPlanForm.get('maxPauseDays')?.value,
        planImage: '',
        discoveryCall: this.therapyPlanForm.get('discoveryCall')?.value
      };
      if (this.planImage) {
        try {
          const uuid = uuidv4().replace(/-/g, '');
          const extension = this.planImage.name.split('.').pop();
          const filePath = `planImages/${uuid}.${extension}`;
          const uploadUrl = await lastValueFrom(
            this.fileService.generateUploadUrl(
              AnyoS3Buckets.CDN_BUCKET,
              filePath,
              this.planImage.type,
            ),
          );
          this.toastService.showSuccess(`Uploading Plan Image`);
          await lastValueFrom(
            this.fileService.uploadFile(this.planImage, uploadUrl.url),
          );
          this.toastService.showSuccess(`Uploading Plan Image completed`);
          createTherapyPlanRequest.planImage = filePath;
        } catch (e) {
          this.toastService.showError(`Uploading Plan Image failed`);
        }
      }
      this.therapyPlanService
        .addTherapyPlan(createTherapyPlanRequest)
        .subscribe({
          next: () => {
            this.pageLoading = false;
            this.toastService.showSuccess(
              `Therapy plan ${createTherapyPlanRequest.planName} created successfully`,
            );
            this.router.navigate(['/therapy-plans/all']);
          },
          error: (error) => {
            this.pageLoading = false;
            const errorBody = error.error as IAnyoError;
            this.toastService.showAnyoErrorToast(errorBody.description);
          },
        });
    } else {
      Object.values(this.therapyPlanForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }
  therapyPlanForm: FormGroup;
  constructor(
    protected override auth: AuthService,
    protected toastService: ToastService,
    private fb: NonNullableFormBuilder,
    private therapyPlanService: TherapyPlansService,
    private masterDataService: MasterdataService,
    private fileService: FileUtilsService,
    private router: Router,
  ) {
    super(auth);

    this.therapyPlanForm = fb.group({
      order: [0, this.nonZeroValidator],
      maxPauseDays: [0],
      internalPlanName: ['', Validators.required],
      canPause: [false, Validators.required],
      activeFrom: [moment().toDate(), Validators.required],
      activeUpto: [],
      active: [true],
      discoveryCall: [true],
      planName: ['', Validators.required],
      planDescription: ['', Validators.required],
      paymentType: ['RETAIL_PAID', Validators.required],
      noOfSessions: [3, this.nonZeroValidator],
      validityDays: [30, this.nonZeroValidator],
      perSessionTimeInMin: [60, this.nonZeroValidator],
      markUpPrice: [0],
      savings: [0],
      perSessionPrice: [0],
    });
    this.onNoOfSessionsChange(3);
  }
  expertLevels: string[] = [];
  therapistExpertLevel: TherapistExpertLevel[] = [];
  nonZeroValidator: ValidatorFn = (control: AbstractControl) => {
    if (control.value === 0) {
      return { nonZero: true };
    }
    return null;
  };
  override async ready(user?: IAnyoUser | undefined): Promise<void> {
    if (user) {
      const masterData = await lastValueFrom(
        this.masterDataService.masterDataList(),
      );
      this.therapistExpertLevel = masterData.therapistExpertLevel;
      this.expertLevels = this.therapistExpertLevel.map((value) => value.data);
      this.expertLevels.forEach((value) => {
        this.charges[value] = [
          {
            currency: 'INR',
            amount: 0,
            currencySymbol: '₹',
            markupAmount: 0,
            savingText: 0,
            perSessionAmount: 0,
          },
        ];
      });
      this.pageLoading = false;
    }

    return;
  }
  noOfSessions: number = 3;
  sessionNameMapping: {
    name: string;
    description: string;
    sessionMinutes?: number;
  }[] = Array(3)
    .fill(0)
    .map((_, i) => ({
      name: `Session ${i + 1}`,
      description: '',
      sessionMinutes: 0,
    }));
  paymentType: TherapyPaymentType = TherapyPaymentType.RETAIL_PAID;
  charges: Record<
    string,
    {
      currency: string;
      amount: number;
      currencySymbol: string;
      markupAmount: number;
      savingText: number;
      perSessionAmount: number;
    }[]
  > = {};
  onNoOfSessionsChange(event: number) {
    this.noOfSessions = Number(event);
    this.sessionNameMapping = Array(event)
      .fill(0)
      .map((_, i) => ({
        name: `Session ${i + 1}`,
        description: '',
        sessionMinutes: this.therapyPlanForm.get('perSessionTimeInMin')?.value || 60,
      }));
  }
  uploadImages($event: Event) {
    const fileInput = $event.target as HTMLInputElement;
    const file = fileInput.files;
    if (file) {
      this.planImage = file[0];
    }
  }
  validateSessionNameMapping(): boolean {
    for (let i = 0; i < this.sessionNameMapping.length; i++) {
      if (!this.sessionNameMapping[i].name) {
        return false;
      }
    }
    return true;
  }

  validatePlanCharges(): boolean {
    for (const level in this.charges) {
      for (let i = 0; i < this.charges[level].length; i++) {
        if (!this.charges[level][i].amount) {
          return false;
        }
      }
    }
    return true;
  }
}
